import {
  Box,
  DataTable,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridEventListener,
  GridSortModel,
  gridClasses,
  useGridApiRef,
} from '@chronosphereio/chrono-ui';
import { useEffect, useMemo } from 'react';
import { getNoDataMessage, useChartsTheme } from '@/components';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, useDataQueryResult, useVizPanelMenuItem } from '@/plugin-system';
import { NoData } from '@/plugin-system/components/NoData';
import {
  DEFAULT_TABLE_HEIGHT,
  sortColumns,
  TableProps,
  transformToColumnVisibilityModel,
  transformToGridSortModel,
} from './table-model';
import { useColumnStyles, useFormattedColumns } from './useFormattedColumns';
import { LINKS_COLUMN_FIELD_IDENTIFIER, useLinksColumn } from './useLinksColumn';
import {
  handleTableColumnOrderChange,
  handleTableColWidthChange,
  handleTableSortChange,
  useTableRowsAndColumns,
  useTransformedColumns,
} from './utils';

export function TablePanelLoaded(props: TableProps) {
  const { spec, width, height, onPluginSpecChange, onRowClick, compact, links } = props;
  const { sort, columnOverrides, thresholds } = spec;

  const chartsTheme = useChartsTheme();
  const { rows, columns, isLoading } = useTableRowsAndColumns(spec);

  const { pagination } = useDataQueryResult({
    dataTypeFilter: 'Table',
  });

  const transformedColumns = useTransformedColumns({ columns, columnOverrides });

  const sortedColumns = useMemo(
    () => sortColumns(transformedColumns, spec.column_order),
    [transformedColumns, spec.column_order]
  );

  const sortModel = useMemo(() => {
    return transformToGridSortModel(sort);
  }, [sort]);

  const formattedColumns = useFormattedColumns(sortedColumns, columnOverrides, thresholds);
  const columnStyles = useColumnStyles(columnOverrides, thresholds);
  const columnsWithLinks = useLinksColumn(links, formattedColumns);

  const columnVisibilityModel = useMemo(() => {
    return transformToColumnVisibilityModel(columnOverrides);
  }, [columnOverrides]);

  const handleSortChange = (newSort: GridSortModel) => {
    if (onPluginSpecChange) {
      handleTableSortChange({ newSort, spec, onPluginSpecChange });
    }
  };

  const handleColWidthChange = (params: GridColumnResizeParams) => {
    if (onPluginSpecChange) {
      handleTableColWidthChange({ params, spec, onPluginSpecChange });
    }
  };

  const handleColumnOrderChange = (params: GridColumnOrderChangeParams) => {
    if (onPluginSpecChange) {
      handleTableColumnOrderChange({ params, columns, spec, onPluginSpecChange });
    }
  };

  const handleRowClick: GridEventListener<'rowClick'> = (params, e) => {
    onRowClick?.(params.row._vfId, params.row, e);
  };

  const apiRef = useGridApiRef();
  const { setMenuItem } = useVizPanelMenuItem('export-csv');

  useEffect(() => {
    if (props.disableCsvExport) {
      return;
    }
    setMenuItem({
      label: 'Export CSV',
      onClick: () => {
        apiRef.current.exportDataAsCsv();
      },
    });
  }, [apiRef, setMenuItem, props.disableCsvExport]);

  if (width === undefined || height === undefined) return null;

  const noDataMessage = getNoDataMessage(chartsTheme);

  const hasNoData = !rows.length || !columns.length;

  if (!isLoading && hasNoData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        width={width}
        height={height ?? DEFAULT_TABLE_HEIGHT}
      >
        <NoData />
      </Box>
    );
  }

  // Do not load DataTable if columns have not been loaded.
  // https://github.com/mui/mui-x/issues/8018
  if (isLoading || !columns.length) return null;

  return (
    <Box height={height} width={width}>
      <DataTable
        enableExpandableCells
        getRowId={(row) => row._vfId}
        pagination={pagination !== undefined}
        paginationMode={pagination ? 'server' : undefined}
        paginationModel={{
          page: pagination?.page ?? DEFAULT_PAGE_NUMBER,
          pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
        }}
        apiRef={apiRef}
        pageSizeOptions={[pagination?.pageSize ?? DEFAULT_PAGE_SIZE]}
        rowCount={pagination?.totalRowCount ?? Number.MAX_VALUE}
        onPaginationModelChange={pagination?.setPagination}
        slotProps={
          // if total row count is known, do nothing
          // else disable next page button if hasNextPage is false
          pagination?.totalRowCount
            ? undefined
            : {
                pagination: {
                  nextIconButtonProps: {
                    disabled: !pagination?.hasNextPage,
                  },
                },
              }
        }
        columns={columnsWithLinks}
        rows={rows}
        compact={compact}
        hideFooterRowCount
        hideFooter={!pagination}
        localeText={{
          noRowsLabel: noDataMessage,
          MuiTablePagination: {
            labelDisplayedRows: ({ from, to, count }) => {
              const hasTotalRowCount = pagination?.totalRowCount !== undefined;
              const adjustedTo = pagination?.hasNextPage ? to : from + rows.length - 1;
              return `${from} - ${hasTotalRowCount ? to : adjustedTo} of ${hasTotalRowCount ? count : 'many'}`;
            },
          },
        }}
        sortModel={sortModel}
        onSortModelChange={handleSortChange}
        columnVisibilityModel={columnVisibilityModel}
        onColumnWidthChange={handleColWidthChange}
        onColumnOrderChange={handleColumnOrderChange}
        onRowClick={handleRowClick}
        sx={{
          // Prevent unnecessary scrollbars when pagination is enabled
          '& .MuiTablePagination-toolbar': {
            height: '50px',
            minHeight: '50px',
          },
          // Wrap in the row to ensure the css specificity is sufficient to "win" over the
          // default styles when using expandable cells.
          [`& .${gridClasses.row}`]: {
            ...columnStyles,
          },
        }}
        pinnedColumns={{
          right: [LINKS_COLUMN_FIELD_IDENTIFIER],
        }}
      />
    </Box>
  );
}
